import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import loader from '../../../assets/Images/loader.gif';

const WHATSAPP_API = 'https://oneml.spectra.co/getWhatsAppTempData';
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// eslint-disable-next-line no-unused-vars -- Time Taken column hidden for now
const timeDiff = (start, end) => {
    if (!start || !end) return '—';
    const a = new Date(start);
    const b = new Date(end);
    if (isNaN(a) || isNaN(b)) return '—';
    const diffMs = Math.abs(b - a);
    if (diffMs === 0) return '0s';
    const secs  = Math.floor(diffMs / 1000) % 60;
    const mins  = Math.floor(diffMs / 60000) % 60;
    const hours = Math.floor(diffMs / 3600000);
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (mins  > 0) parts.push(`${mins}m`);
    if (secs  > 0 || parts.length === 0) parts.push(`${secs}s`);
    return parts.join(' ');
};

const stripCountryCode = (num) => {
    if (!num) return '';
    const s = String(num).trim();
    return s.startsWith('91') && s.length > 10 ? s.slice(2) : s;
};

const COPYABLE_COLUMNS = ['campaign_id', 'message_id'];

const copyToClipboard = async (text, label) => {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
        } else {
            // fallback for http / older webviews
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
        }
        toast.success(`${label} copied`, { position: toast.POSITION.BOTTOM_CENTER, autoClose: 1200 });
    } catch (error) {
        console.error('copy error:', error);
        toast.error('Unable to copy', { position: toast.POSITION.BOTTOM_CENTER, autoClose: 1500 });
    }
};

const CopyButton = ({ text, label }) => (
    <button
        onClick={() => copyToClipboard(text, label)}
        title={`Copy ${label}`}
        style={{
            background: '#f0eaf8', border: 'none', borderRadius: '6px',
            color: 'var(--bg-color)', padding: '3px 7px', cursor: 'pointer',
            fontSize: '0.72rem', lineHeight: 1, flexShrink: 0,
        }}
    >
        <i className="fa-regular fa-copy" />
    </button>
);

// columns whose cell should not be clipped, so the copy button stays visible
const isUnclippedColumn = (col) => {
    const lk = col.toLowerCase();
    return COPYABLE_COLUMNS.includes(lk) || lk.includes('error');
};

const STATUS_MAP = {
    STATUS_DELIVERED:             { bg: '#d1f0d8', color: '#1a6b2e', label: 'Delivered' },
    STATUS_READ:                  { bg: '#cce5ff', color: '#004085', label: 'Read' },
    STATUS_SENT:                  { bg: '#fff3cd', color: '#856404', label: 'Sent' },
    STATUS_FAILED:                { bg: '#f8d7da', color: '#721c24', label: 'Failed' },
    STATUS_REPLIED:               { bg: '#e8d5f5', color: '#5a1a8a', label: 'Replied' },
    STATUS_CONVERSATION_CREATED:  { bg: '#fde8c8', color: '#7a4800', label: 'Conversation Created' },
};


const statusColor = (status) => {
    if (!status) return { bg: '#e9e9e9', color: '#555', label: '—' };
    return STATUS_MAP[status.trim()] || { bg: '#e9e9e9', color: '#555', label: status };
};

const StatusBadge = ({ value }) => {
    const { bg, color, label } = statusColor(value);
    return (
        <span style={{
            display: 'inline-block', padding: '2px 10px', borderRadius: '12px',
            fontSize: '0.72rem', fontWeight: 600, background: bg, color,
            whiteSpace: 'nowrap', letterSpacing: '0.03em',
        }}>
            {label}
        </span>
    );
};

const ERROR_DESCRIPTIONS = {
    '131049': 'Meta Error 131049 ("This message was not delivered to maintain healthy ecosystem engagement") means WhatsApp has blocked a message to protect the recipient from spam. This happens automatically when a user receives too many promotional messages in a short timeframe or rarely replies to your business.',
    '131026': 'Meta Error 131026 indicates a "Message Undeliverable" status on the WhatsApp Business API. It usually means WhatsApp could not successfully push the message to the end-user. 1. Recipient Number Issues — Not on WhatsApp: The phone number you are targeting is not registered on the WhatsApp network. Invalid Format: Ensure the number is formatted correctly with the full country code (e.g., +91 for India) and no leading zeros or spaces.',
    '130472': 'Meta Error Code 130472 means the recipient\'s phone number is temporarily placed in a random user test group as part of an ongoing platform experiment. You cannot send marketing template messages to this user until they initiate a conversation with your business.',
    '131050': 'Meta Error Code 131050 indicates that the customer has explicitly opted out of receiving marketing messages from your business via WhatsApp. Meta does not allow you to override this. Why This Happens — Customer Preference: The user has manually toggled off "offers and announcements" or blocked your business\'s marketing messages in their chat settings. Automated Restrictions: If your business account has received too many spam reports or low-quality ratings, Meta might automatically restrict marketing delivery. Policy Violations: The message may have triggered Meta\'s strict Business or Commerce Policies.',
};

const WhatsappData = () => {
    sessionStorage.setItem("userEmail", "crm.support@spectra.co")
    sessionStorage.setItem("userName", "crm.support")
    sessionStorage.setItem("password", "Spectra")

    const [allData, setAllData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasFetched, setHasFetched] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [activeStatus, setActiveStatus] = useState('All');
    const [showErrorView, setShowErrorView] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const response = await fetch(WHATSAPP_API, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                });
                const result = await response.json();
                const rows = result?.data || result?.Data || (Array.isArray(result) ? result : []);
                if (rows.length > 0) {
                    setAllData(rows);
                } else {
                    toast.warning(result?.meta?.Message || 'No data found', {
                        position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000,
                    });
                }
            } catch (error) {
                console.error('getWhatsAppTempData error:', error);
                toast.error('Failed to fetch WhatsApp data.', {
                    position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000,
                });
            } finally {
                setIsLoading(false);
                setHasFetched(true);
            }
        };
        fetchData();
    }, []);

    const statuses = useMemo(() => {
        const unique = [...new Set(allData.map((r) => r.status || r.Status || '').filter(Boolean))];
        return ['All', ...unique];
    }, [allData]);

    const errorColumnKey = useMemo(() => {
        if (allData.length === 0) return null;
        return Object.keys(allData[0]).find((k) => k.toLowerCase().includes('error')) || null;
    }, [allData]);

    useEffect(() => { setPage(1); }, [searchInput, activeStatus, pageSize, dateFrom, dateTo]);

    const filteredData = useMemo(() => {
        return allData.filter((r) => {
            const rawMobile = r.mobile_number || r.mobileNumber || r.to_mobile || r.mobile || r.phone || '';
            const displayMobile = stripCountryCode(rawMobile);
            const term = searchInput.trim().toLowerCase();
            const matchesSearch = !term
                || displayMobile.includes(term)
                || String(r.campaign_id || '').toLowerCase().includes(term)
                || String(r.message_id || '').toLowerCase().includes(term);
            const rowStatus = r.status || r.Status || '';
            const matchesStatus = activeStatus === 'All' || rowStatus === activeStatus;

            let matchesDate = true;
            if (dateFrom || dateTo) {
                const dateVal = r.create_at || r.created_at || r.createdAt || '';
                if (!dateVal) {
                    matchesDate = false;
                } else {
                    const rowDate = new Date(dateVal);
                    if (dateFrom) {
                        const from = new Date(dateFrom);
                        from.setHours(0, 0, 0, 0);
                        if (rowDate < from) matchesDate = false;
                    }
                    if (dateTo && matchesDate) {
                        const to = new Date(dateTo);
                        to.setHours(23, 59, 59, 999);
                        if (rowDate > to) matchesDate = false;
                    }
                }
            }

            return matchesSearch && matchesStatus && matchesDate;
        });
    }, [allData, searchInput, activeStatus, dateFrom, dateTo]);

    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    const pageData = filteredData.slice((page - 1) * pageSize, page * pageSize);
    const startRecord = filteredData.length === 0 ? 0 : (page - 1) * pageSize + 1;
    const endRecord = Math.min(page * pageSize, filteredData.length);

    const pageNumbers = useMemo(() => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= page - 1 && i <= page + 1)) {
                pages.push(i);
            } else if (pages[pages.length - 1] !== '...') {
                pages.push('...');
            }
        }
        return pages;
    }, [totalPages, page]);

    const columns = useMemo(() => {
        if (allData.length === 0) return [];
        const sample = allData[0];
        return Object.keys(sample).filter((k) => {
            const lk = k.toLowerCase();
            // retry count hidden for now
            return !['__v', '_id', 'id'].includes(lk) && !lk.includes('vendor') && lk.replace(/[_\s]/g, '') !== 'retrycount';
        });
    }, [allData]);

    const parseJsonField = (value) => {
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed) && parsed.length > 0) {
                const first = parsed[0];
                const msg = first.title || first.message || first.Message || first.description || JSON.stringify(first);
                const code = first.code || first.Code || first.errorCode || first.error_code || null;
                return code ? { message: msg, code } : msg;
            }
            if (typeof parsed === 'object' && parsed !== null) {
                const inner = parsed.Message || parsed.message || parsed.title;
                const code = parsed.code || parsed.Code || parsed.errorCode || parsed.error_code || null;
                if (inner) {
                    const innerResult = parseJsonField(inner);
                    if (innerResult && typeof innerResult === 'object') {
                        return { message: innerResult.message, code: innerResult.code || code };
                    }
                    return code ? { message: innerResult, code } : innerResult;
                }
                return code ? { message: JSON.stringify(parsed), code } : JSON.stringify(parsed);
            }
            return String(parsed);
        } catch {
            return null;
        }
    };

    const errorCodeData = useMemo(() => {
        if (!errorColumnKey) return [];
        const map = new Map();
        allData.forEach((r) => {
            const val = r[errorColumnKey];
            if (!val) return;
            const str = String(val);
            const parsed = (str.startsWith('{') || str.startsWith('[')) ? parseJsonField(str) : null;
            let code = null, message = str;
            if (parsed && typeof parsed === 'object') {
                code = parsed.code || null;
                message = parsed.message || str;
            } else if (parsed) {
                message = String(parsed);
            }
            const key = code || message;
            if (!map.has(key)) {
                map.set(key, { code, message, count: 1 });
            } else {
                map.get(key).count++;
            }
        });
        return [...map.values()];
    }, [allData, errorColumnKey]);

    const formatCell = (key, value) => {
        if (value === null || value === undefined || value === '') return '—';
        const lk = key.toLowerCase();
        const isMobile = lk.includes('mobile') || lk.includes('phone') || lk === 'to' || lk === 'from';
        if (isMobile) return stripCountryCode(value);
        if (lk === 'status') return <StatusBadge value={value} />;
        if (COPYABLE_COLUMNS.includes(lk)) {
            return (
                <span className="d-inline-flex align-items-center gap-2">
                    <span style={{ color: '#666', fontFamily: 'monospace', fontSize: '0.74rem', userSelect: 'all' }}>
                        {String(value)}
                    </span>
                    <CopyButton text={String(value)} label={formatHeader(key)} />
                </span>
            );
        }

        const str = String(value);
        const jsonParsed = str.startsWith('{') || str.startsWith('[') ? parseJsonField(str) : null;

        let display, errorCode;
        if (jsonParsed !== null && typeof jsonParsed === 'object') {
            display = jsonParsed.message || str;
            errorCode = jsonParsed.code;
        } else {
            display = jsonParsed !== null ? jsonParsed : str;
            errorCode = null;
        }

        const truncated = display.length > 40 ? display.slice(0, 40) + '…' : display;
        const fullTitle = errorCode ? `[${errorCode}] ${display}` : display;

        const isError = lk.includes('error');

        return (
            <span className={isError ? 'd-inline-flex align-items-center gap-2' : undefined}>
                <span title={fullTitle} style={{ cursor: display.length > 40 ? 'help' : 'default' }}>
                    {errorCode && (
                        <span style={{
                            display: 'inline-block', marginRight: '5px', padding: '1px 6px',
                            borderRadius: '4px', fontSize: '0.7rem', fontWeight: 700,
                            background: '#f8d7da', color: '#721c24', whiteSpace: 'nowrap',
                        }}>
                            {errorCode}
                        </span>
                    )}
                    {truncated}
                </span>
                {isError && <CopyButton text={fullTitle} label={formatHeader(key)} />}
            </span>
        );
    };

    const formatHeader = (key) =>
        key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()
            .split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    const exportCSV = () => {
        if (filteredData.length === 0) return;
        const headers = columns.map(formatHeader); // 'Time Taken' hidden for now
        const escape = (val) => {
            const s = String(val ?? '').replace(/"/g, '""');
            return /[",\n]/.test(s) ? `"${s}"` : s;
        };
        const rows = filteredData.map((r) => {
            // const createVal = r.create_at || r.created_at || r.createdAt || '';
            // const updateVal = r.update_time || r.updated_at || r.updatedAt || r.update_at || '';
            const cells = columns.map((col) => {
                const value = r[col];
                if (value === null || value === undefined || value === '') return '';
                const lk = col.toLowerCase();
                const isMobile = lk.includes('mobile') || lk.includes('phone') || lk === 'to' || lk === 'from';
                if (isMobile) return escape(stripCountryCode(value));
                const str = String(value);
                const jsonParsed = str.startsWith('{') || str.startsWith('[') ? parseJsonField(str) : null;
                if (jsonParsed !== null && typeof jsonParsed === 'object') {
                    return escape(jsonParsed.code ? `[${jsonParsed.code}] ${jsonParsed.message}` : jsonParsed.message);
                }
                return escape(jsonParsed !== null ? jsonParsed : str);
            });
            // cells.push(escape(timeDiff(createVal, updateVal))); // Time Taken hidden for now
            return cells.join(',');
        });
        const csv = [headers.map(escape).join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `whatsapp_data_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <>
            {isLoading && (
                <div className="loader-wrapper">
                    <div className="loader">
                        <img src={loader} alt="loading..." />
                    </div>
                </div>
            )}

            {/* Filter Panel */}
            <div className="Searchbar-bg" style={{
                padding: '8px 16px',
                marginTop: '63px',
                marginBottom: '16px',
                position: 'sticky',
                top: '63px',
                zIndex: 999,
            }}>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                    {/* Search */}
                    <div style={{ position: 'relative', flex: '1 1 200px', minWidth: '180px', maxWidth: '340px' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{
                            position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
                            color: '#bbb', fontSize: '0.78rem', pointerEvents: 'none',
                        }} />
                        <input
                            type="text"
                            placeholder="Search mobile, campaign ID or message ID…"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            style={{
                                width: '100%', padding: '8px 30px 8px 30px',
                                borderRadius: '6px', border: 'none',
                                fontSize: '0.83rem', background: '#fff',
                                outline: 'none',
                            }}
                        />
                        {searchInput && (
                            <button onClick={() => setSearchInput('')} style={{
                                position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                                background: 'none', border: 'none', color: '#aaa', cursor: 'pointer', padding: 0,
                            }}>
                                <i className="fa-solid fa-xmark" style={{ fontSize: '0.75rem' }} />
                            </button>
                        )}
                    </div>

                    {/* Date range */}
                    <div className="d-flex align-items-center gap-2" style={{ flexWrap: 'nowrap' }}>
                        <div style={{ position: 'relative' }}>
                            <i className="fa-regular fa-calendar" style={{
                                position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)',
                                color: '#bbb', fontSize: '0.72rem', pointerEvents: 'none',
                            }} />
                            <input
                                type="date"
                                value={dateFrom}
                                max={dateTo || undefined}
                                onChange={(e) => setDateFrom(e.target.value)}
                                style={{
                                    padding: '8px 10px 8px 26px', borderRadius: '6px', border: 'none',
                                    fontSize: '0.8rem', background: '#fff',
                                    outline: 'none', width: '148px',
                                    color: dateFrom ? '#333' : '#999',
                                }}
                            />
                        </div>
                        <span style={{ color: 'var(--bg-color)', fontSize: '0.9rem', fontWeight: 500 }}>→</span>
                        <div style={{ position: 'relative' }}>
                            <i className="fa-regular fa-calendar" style={{
                                position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)',
                                color: '#bbb', fontSize: '0.72rem', pointerEvents: 'none',
                            }} />
                            <input
                                type="date"
                                value={dateTo}
                                min={dateFrom || undefined}
                                onChange={(e) => setDateTo(e.target.value)}
                                style={{
                                    padding: '8px 10px 8px 26px', borderRadius: '6px', border: 'none',
                                    fontSize: '0.8rem', background: '#fff',
                                    outline: 'none', width: '148px',
                                    color: dateTo ? '#333' : '#999',
                                }}
                            />
                        </div>
                        {(dateFrom || dateTo) && (
                            <button onClick={() => { setDateFrom(''); setDateTo(''); }} style={{
                                background: '#f0eaf8', border: 'none',
                                borderRadius: '6px', color: 'var(--bg-color)', padding: '5px 9px',
                                cursor: 'pointer', fontSize: '0.75rem', lineHeight: 1,
                            }} title="Clear dates">
                                <i className="fa-solid fa-xmark" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="container-fluid px-3">

                {/* Status filter pills */}
                {hasFetched && statuses.length > 1 && (
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        {statuses.map((s) => {
                            const isActive = activeStatus === s;
                            const { bg, color } = s === 'All'
                                ? { bg: 'var(--bg-color)', color: '#fff' }
                                : statusColor(s);
                            const count = s === 'All'
                                ? allData.length
                                : allData.filter((r) => (r.status || r.Status || '') === s).length;
                            return (
                                <button
                                    key={s}
                                    onClick={() => { setActiveStatus(s); setShowErrorView(false); }}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        borderRadius: '20px',
                                        border: isActive ? 'none' : '1px solid #dde',
                                        background: isActive ? (s === 'All' ? 'var(--bg-color)' : bg) : '#fff',
                                        color: isActive ? (s === 'All' ? '#fff' : color) : '#666',
                                        fontWeight: isActive ? 700 : 400,
                                        fontSize: '0.78rem',
                                        padding: '5px 14px',
                                        cursor: 'pointer',
                                        transition: 'all 0.15s',
                                        whiteSpace: 'nowrap',
                                        boxShadow: isActive ? '0 3px 10px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.07)',
                                    }}
                                >
                                    {s}
                                    <span style={{
                                        fontSize: '0.68rem', fontWeight: 700,
                                        background: isActive ? 'rgba(255,255,255,0.25)' : '#f0eaf8',
                                        color: isActive ? (s === 'All' ? '#fff' : color) : 'var(--bg-color)',
                                        borderRadius: '10px', padding: '1px 7px',
                                        minWidth: '22px', textAlign: 'center',
                                    }}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Error Description toggle */}
                {hasFetched && errorColumnKey && (
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <button
                            onClick={() => setShowErrorView((v) => !v)}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                borderRadius: '20px',
                                border: showErrorView ? 'none' : '1px solid #f5c6cb',
                                background: showErrorView ? '#f8d7da' : '#fff5f5',
                                color: '#721c24',
                                fontWeight: showErrorView ? 700 : 500,
                                fontSize: '0.78rem',
                                padding: '5px 16px',
                                cursor: 'pointer',
                                transition: 'all 0.15s',
                                boxShadow: showErrorView ? '0 2px 8px rgba(114,28,36,0.2)' : '0 1px 3px rgba(0,0,0,0.07)',
                            }}
                        >
                            <i className="fa-solid fa-triangle-exclamation" />
                            Error Description
                            {showErrorView && <i className="fa-solid fa-xmark" style={{ fontSize: '0.68rem' }} />}
                        </button>
                    </div>
                )}

                {/* Toolbar */}
                {hasFetched && !isLoading && allData.length > 0 && !showErrorView && (
                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2"
                        style={{ background: '#f7f4fc', borderRadius: '10px', padding: '8px 14px', border: '1px solid #ede8f8' }}>
                        <div className="d-flex align-items-center gap-2">
                            <span style={{
                                background: 'var(--bg-color)', color: '#fff',
                                borderRadius: '8px', padding: '2px 10px',
                                fontSize: '0.72rem', fontWeight: 700,
                            }}>
                                {filteredData.length}
                            </span>
                            <small style={{ color: '#888', fontSize: '0.78rem' }}>
                                {filteredData.length === 0
                                    ? 'no records match'
                                    : `records${(searchInput || activeStatus !== 'All' || dateFrom || dateTo) ? ` · filtered from ${allData.length}` : ''} · showing ${startRecord}–${endRecord}`}
                            </small>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <div className="d-flex align-items-center gap-1">
                                <small style={{ color: '#aaa', fontSize: '0.72rem', marginRight: '2px' }}>Rows:</small>
                                {PAGE_SIZE_OPTIONS.map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setPageSize(s)}
                                        style={{
                                            padding: '2px 8px', fontSize: '0.75rem', borderRadius: '6px',
                                            border: 'none', cursor: 'pointer',
                                            background: pageSize === s ? 'var(--bg-color)' : '#ede8f8',
                                            color: pageSize === s ? '#fff' : 'var(--bg-color)',
                                            fontWeight: pageSize === s ? 700 : 400,
                                            transition: 'all 0.12s',
                                        }}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                            <button
                                onClick={exportCSV}
                                disabled={filteredData.length === 0}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                                    padding: '5px 14px', fontSize: '0.78rem', borderRadius: '8px',
                                    border: 'none', cursor: filteredData.length === 0 ? 'not-allowed' : 'pointer',
                                    background: filteredData.length === 0
                                        ? '#e9e9e9'
                                        : 'linear-gradient(135deg, #28a745 0%, #1a7a30 100%)',
                                    color: filteredData.length === 0 ? '#aaa' : '#fff',
                                    fontWeight: 600,
                                    boxShadow: filteredData.length > 0 ? '0 2px 8px rgba(40,167,69,0.3)' : 'none',
                                    transition: 'all 0.15s',
                                }}
                                title="Export filtered data as CSV"
                            >
                                <i className="fa-solid fa-file-csv" />
                                Export CSV
                            </button>
                        </div>
                    </div>
                )}

                {/* No data */}
                {hasFetched && !isLoading && allData.length === 0 && !showErrorView && (
                    <div style={{ textAlign: 'center', padding: '64px 20px' }}>
                        <div style={{
                            width: '68px', height: '68px', borderRadius: '50%',
                            background: 'linear-gradient(135deg, #f0eaf8, #e2d5f4)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 14px',
                        }}>
                            <i className="fa-brands fa-whatsapp" style={{ fontSize: '2rem', color: 'var(--bg-color)', opacity: 0.45 }} />
                        </div>
                        <p style={{ fontSize: '0.95rem', color: '#aaa', margin: 0 }}>No WhatsApp log data available.</p>
                    </div>
                )}

                {/* No match */}
                {hasFetched && !isLoading && allData.length > 0 && filteredData.length === 0 && !showErrorView && (
                    <div style={{ textAlign: 'center', padding: '54px 20px' }}>
                        <div style={{
                            width: '58px', height: '58px', borderRadius: '50%',
                            background: '#f5f5f5',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 12px',
                        }}>
                            <i className="fa-solid fa-magnifying-glass" style={{ fontSize: '1.4rem', color: '#ccc' }} />
                        </div>
                        <p style={{ fontSize: '0.9rem', color: '#aaa', margin: '0 0 4px' }}>No records match your filters.</p>
                        <small style={{ color: '#bbb' }}>Try adjusting the search or date range.</small>
                    </div>
                )}

                {/* Error codes view */}
                {showErrorView && hasFetched && (
                    <div style={{ borderRadius: '12px', overflow: 'hidden', boxShadow: '0 3px 14px rgba(0,0,0,0.1)' }}>
                        <table className="table table-hover mb-0" style={{ fontSize: '0.83rem' }}>
                            <thead>
                                <tr style={{
                                    background: 'linear-gradient(135deg, #7b1c24 0%, #a93226 100%)',
                                    color: '#fff',
                                }}>
                                    <th style={thStyle}>#</th>
                                    <th style={thStyle}>Error Code</th>
                                    <th style={thStyle}>Message</th>
                                    <th style={thStyle}>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                {errorCodeData.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} style={{ textAlign: 'center', color: '#aaa', padding: '24px' }}>No error data found.</td>
                                    </tr>
                                ) : (
                                    errorCodeData.map((item, idx) => (
                                        <tr key={idx} style={{
                                            verticalAlign: 'middle',
                                            background: idx % 2 === 0 ? '#fff' : '#fdf8f8',
                                        }}>
                                            <td style={{ ...tdStyle, color: '#ccc', fontWeight: 600 }}>{idx + 1}</td>
                                            <td style={tdStyle}>
                                                {item.code ? (
                                                    <span style={{
                                                        display: 'inline-block', padding: '2px 10px',
                                                        borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700,
                                                        background: '#f8d7da', color: '#721c24',
                                                    }}>
                                                        {item.code}
                                                    </span>
                                                ) : '—'}
                                            </td>
                                            <td style={{ ...tdStyle, maxWidth: '300px', whiteSpace: 'normal' }}>{item.message}</td>
                                            <td style={{
                                                ...tdStyle, maxWidth: '400px', whiteSpace: 'normal', lineHeight: 1.5,
                                                color: item.code && ERROR_DESCRIPTIONS[String(item.code)] ? '#444' : '#bbb',
                                                fontStyle: item.code && ERROR_DESCRIPTIONS[String(item.code)] ? 'normal' : 'italic',
                                            }}>
                                                {item.code && ERROR_DESCRIPTIONS[String(item.code)]
                                                    ? ERROR_DESCRIPTIONS[String(item.code)]
                                                    : '—'}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Main Table */}
                {hasFetched && !isLoading && pageData.length > 0 && !showErrorView && (
                    <div style={{ borderRadius: '12px', overflow: 'hidden', boxShadow: '0 3px 14px rgba(0,0,0,0.09)' }}>
                        <div className="table-responsive">
                            <table className="table table-hover mb-0" style={{ fontSize: '0.82rem' }}>
                                <thead>
                                    <tr style={{
                                        backgroundColor: 'var(--bg-color)',
                                        color: '#fff',
                                    }}>
                                        <th style={{ ...thStyle, width: '42px', textAlign: 'center' }}>#</th>
                                        {columns.map((col) => (
                                            <th key={col} style={thStyle}>{formatHeader(col)}</th>
                                        ))}
                                        {/* Time Taken hidden for now */}
                                        {/* <th style={thStyle}>Time Taken</th> */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageData.map((r, idx) => {
                                        // const createVal = r.create_at || r.created_at || r.createdAt || '';
                                        // const updateVal = r.update_time || r.updated_at || r.updatedAt || r.update_at || '';
                                        // const diff = timeDiff(createVal, updateVal);
                                        return (
                                            <tr key={idx} style={{ verticalAlign: 'middle', background: idx % 2 === 0 ? '#fff' : '#faf8fd' }}>
                                                <td style={{ ...tdStyle, textAlign: 'center', color: '#ccc', fontWeight: 700, width: '42px' }}>
                                                    {startRecord + idx}
                                                </td>
                                                {columns.map((col) => (
                                                    <td key={col} style={isUnclippedColumn(col) ? { ...tdStyle, maxWidth: 'none', overflow: 'visible' } : tdStyle}>
                                                        {formatCell(col, r[col])}
                                                    </td>
                                                ))}
                                                {/* Time Taken hidden for now
                                                <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                                    <span title={`${createVal} → ${updateVal}`} style={{
                                                        display: 'inline-block', padding: '2px 9px', borderRadius: '10px',
                                                        background: diff === '—' ? '#f2f2f2' : '#eaf4ff',
                                                        color: diff === '—' ? '#bbb' : '#004085',
                                                        fontSize: '0.73rem', fontWeight: 600,
                                                        cursor: createVal && updateVal ? 'help' : 'default',
                                                    }}>
                                                        {diff}
                                                    </span>
                                                </td> */}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Pagination */}
                {hasFetched && !isLoading && filteredData.length > 0 && totalPages > 1 && !showErrorView && (
                    <div className="d-flex justify-content-center align-items-center mt-3 gap-1 flex-wrap">
                        <button style={pagerBtn(false, page === 1)} onClick={() => setPage(1)} disabled={page === 1} title="First">
                            <i className="fa-solid fa-angles-left" />
                        </button>
                        <button style={pagerBtn(false, page === 1)} onClick={() => setPage((p) => p - 1)} disabled={page === 1} title="Prev">
                            <i className="fa-solid fa-angle-left" />
                        </button>
                        {pageNumbers.map((p, i) =>
                            p === '...' ? (
                                <span key={`e-${i}`} style={{ padding: '0 4px', color: '#bbb', fontSize: '0.85rem' }}>…</span>
                            ) : (
                                <button key={p} style={pagerBtn(p === page, false)} onClick={() => setPage(p)}>{p}</button>
                            )
                        )}
                        <button style={pagerBtn(false, page === totalPages)} onClick={() => setPage((p) => p + 1)} disabled={page === totalPages} title="Next">
                            <i className="fa-solid fa-angle-right" />
                        </button>
                        <button style={pagerBtn(false, page === totalPages)} onClick={() => setPage(totalPages)} disabled={page === totalPages} title="Last">
                            <i className="fa-solid fa-angles-right" />
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

const thStyle = {
    whiteSpace: 'nowrap',
    padding: '10px 12px',
    fontWeight: 600,
    fontSize: '0.77rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    border: 'none',
};

const tdStyle = {
    whiteSpace: 'nowrap',
    padding: '9px 12px',
    color: '#444',
    maxWidth: '220px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    borderColor: '#f0ecfa',
};

const pagerBtn = (active, disabled) => ({
    padding: '5px 11px',
    fontSize: '0.78rem',
    borderRadius: '8px',
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    background: active ? 'var(--bg-color)' : disabled ? '#f0f0f0' : '#f0eaf8',
    color: active ? '#fff' : disabled ? '#bbb' : 'var(--bg-color)',
    fontWeight: active ? 700 : 400,
    minWidth: '34px',
    textAlign: 'center',
    transition: 'all 0.12s',
    boxShadow: active ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
});

export default WhatsappData;
